import React, { useMemo, memo } from "react";
import type { BiddingInfoResult } from "../../../types";
import { useTruncatedTooltip } from "../../common/TruncatedTooltip";
import Big from "big.js";
import { BiddingMonitorItemDetails } from "./BiddingMonitorItemDetails"; // 詳細コンポーネントをインポート

//----------------
// Props
//----------------
interface BiddingMonitorItemRowProps {
  item: BiddingInfoResult;
  isExpanded: boolean;
  onToggle: () => void;
}

//--------------------------------
// 監視画面の行コンポーネント (Admin専用)
//--------------------------------
export const BiddingMonitorItemRow = memo(
  ({ item, isExpanded, onToggle }: BiddingMonitorItemRowProps) => {
    const { tooltipProps, TooltipElement } = useTruncatedTooltip(
      "font-medium truncate pointer-events-auto"
    );

    //----------------
    // Memo
    //----------------
    // 総量計算
    const totalWeight = useMemo(() => {
      try {
        const c1 = new Big(item.capacity1 || 0);
        const q1 = new Big(item.quantity1 || 0);
        const c2 = new Big(item.capacity2 || 0);
        const q2 = new Big(item.quantity2 || 0);
        return c1.times(q1).plus(c2.times(q2)).toNumber();
      } catch (e) {
        return 0;
      }
    }, [item.capacity1, item.quantity1, item.capacity2, item.quantity2]);

    // 入札件数の取得
    const bidCount = item.biddingInfoDatas?.length || 0;

    // 行クリック時のハンドラ
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggle();
    };

    //----------------
    // Render
    //----------------
    return (
      <div
        className={`mt-2 lg:mt-4 border-b lg:pb-0 border-slate-800 lg:border lg:rounded-lg lg:shadow-sm overflow-hidden bg-white transition-colors ${
          isExpanded ? "ring-2 ring-indigo-500 rounded-lg" : "hover:bg-slate-50"
        }`}
      >
        {/* === ヘッダー行 (常に表示) === */}
        <div className="p-2 lg:p-4 cursor-pointer relative" onClick={handleClick}>
          <div className={`grid grid-cols-5 md:grid-cols-16 gap-2 items-center text-sm pointer-events-none pr-6 sm:pr-0`}>
            
            {/* 1. 入札番号 */}
            <div className="col-span-1">
              <p className="hidden md:block text-xs text-slate-500 mb-0.5">入札No.</p>
              <p className="text-base lg:font-bold text-slate-800">{item.biddingNumber || "-"}</p>
            </div>

            {/* 2. 売り手 */}
            <div className="col-span-2 md:col-span-2">
              <p className="hidden md:block text-xs text-slate-500 mb-0.5">売手</p>
              <p {...tooltipProps} className="text-lg lg:font-bold pointer-events-auto truncate">{item.sellerName || "-"}</p>
            </div>

            {/* 3. 生産者 */}
            <div className="col-span-2 md:col-span-2">
              <p className="hidden lg:block text-xs text-slate-500 mb-0.5">生産者</p>
              <p {...tooltipProps} className="text-lg lg:font-bold pointer-events-auto truncate">{item.farmerName || "-"}</p>
            </div>

            {/* 7. 荷印 */}
            <div className="col-span-2 md:col-span-2 text-left md:pl-2">
              <p className="hidden lg:block text-xs text-slate-500 mb-0.5">荷印</p>
              <p {...tooltipProps} className="text-lg lg:font-bold truncate">{item.brand || "-"}</p>
            </div>

            {/* 4. 茶期 (PCのみ) */}
            <div className="col-span-1 hidden md:block">
              <p className="text-xs text-slate-500 mb-0.5">茶期</p>
              <p {...tooltipProps} className="text-lg font-bold truncate">{item.teaSeasonName || "-"}</p>
            </div>

            {/* 5. 茶種 (PCのみ) */}
            <div className="col-span-1 hidden md:block">
              <p className="text-xs text-slate-500 mb-0.5">茶種</p>
              <p {...tooltipProps} className="text-lg font-bold truncate">{item.teaTypeName || "-"}</p>
            </div>

            {/* 6. 品種 (PCのみ) */}
            <div className="col-span-1 hidden md:block">
              <p className="text-xs text-slate-500 mb-0.5">品種名</p>
              <p {...tooltipProps} className="text-lg font-bold truncate">{item.varietyName || "-"}</p>
            </div>

            {/* 8. 総量 */}
            <div className="hidden lg:block col-span-1 text-right border-l border-slate-100 pl-2">
              <p className="text-xs text-slate-500 mb-0.5">総量(kg)</p>
              <p className="text-lg lg:font-bold">{totalWeight.toLocaleString()}</p>
            </div>

            {/* 9. 入札件数 */}
            <div className="hidden lg:block col-span-1 text-center border-l border-slate-100 pl-2">
              <p className="text-xs text-slate-500 mb-0.5">入札件数</p>
              <p className="text-lg font-bold text-slate-700">
                {bidCount > 0 ? (
                  <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full text-sm">{bidCount}件</span>
                ) : (
                  <span className="text-slate-400 text-sm">0件</span>
                )}
              </p>
            </div>

            {/* 10. 最低落札値 */}
            <div className="hidden lg:block col-span-2 text-right border-l border-slate-100 pl-2">
              <p className="text-[11px] text-slate-500 mb-0.5 font-bold">最低落札値(円)</p>
              <p className="text-lg font-bold tracking-wide">
                {Number(item.bidMinPrice) > 0 ? Number(item.bidMinPrice).toLocaleString() : "-"}
              </p>
            </div>

            {/* 11. 現在の最高額 */}
            <div className="hidden lg:block col-span-2 text-right border-l border-slate-100 pl-2">
              <p className="text-[11px] text-slate-500 mb-0.5 font-bold">最高入札値(円)</p>
              <p className="text-lg font-black text-slate-900 tracking-wide">
                {Number(item.highestBidPrice) > 0 ? Number(item.highestBidPrice).toLocaleString() : <span className="text-slate-400 text-lg">-</span>}
              </p>
            </div>

            {/* スマホ用展開アイコン */}
            <div className="absolute top-4 right-4 md:hidden pointer-events-none">
              <svg className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* --- モバイル用：金額と件数の表示 --- */}
        <div className="lg:hidden w-full mt-1 mb-2 px-2">
          <div className="flex items-center w-full bg-slate-50 rounded-lg py-2 divide-x divide-slate-300 border border-slate-200">
            <div className="flex-1 px-2 text-center">
              <p className="text-xs text-slate-500 mb-0.5">入札件数</p>
              <p className="text-base font-bold text-indigo-700">{bidCount}件</p>
            </div>
            <div className="flex-1 px-1 text-center flex flex-col justify-center">
              <p className="text-xs text-slate-500 mb-0.5">最低落札値(円)</p>
              <p className="text-xl sm:text-base font-bold text-slate-600 tracking-tight">
                {Number(item.bidMinPrice) > 0 ? Number(item.bidMinPrice).toLocaleString() : "-"}
              </p>
            </div>
            <div className="flex-1 px-2 text-center">
              <p className="text-xs text-slate-500 mb-0.5">最高入札値(円)</p>
              <p className="text-xl font-black text-slate-900 tracking-wide">
                {Number(item.highestBidPrice) > 0 ? Number(item.highestBidPrice).toLocaleString() : <span className="text-slate-400 text-lg">-</span>}
              </p>
            </div>
          </div>
        </div>

        {/* モバイル用: 隠れている項目の簡易表示 */}
        <div className="md:hidden pb-2 text-sm flex flex-wrap gap-x-2 text-slate-600 pointer-events-none justify-left ml-3">
          <span>{item.teaSeasonName}</span><span>/</span>
          <span>{item.teaTypeName}</span><span>/</span>
          <span>{item.varietyName}</span><span>/</span>
          <span>{totalWeight.toLocaleString()}kg</span>
        </div>

        {/*  アコーディオン詳細  */}
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? "max-h-[2000px]" : "max-h-0"}`}>
          {isExpanded && (
            <BiddingMonitorItemDetails item={item} displayInfo={{ totalCapacity: totalWeight }} />
          )}
        </div>

        {TooltipElement}
      </div>
    );
  }
);

export default BiddingMonitorItemRow;